import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Coffee, Terminal, Clipboard, Users, Shield, ArrowLeft } from 'lucide-react';

const AboutPage: React.FC = () => {
  const [currentQuote, setCurrentQuote] = useState(0);

  // The Operator's quotes for the interactive element
  const operatorQuotes = [
    "I don't always test my backups, but when I do, it's in production.",
    "The PFY just discovered why we don't give users sudo. It's educational.",
    "Uptime: 47 days. Excuses prepared: 23. Coffee consumed: uncountable.",
    "Documentation is ammunition. The Operator's Notes are a weapon.",
    "Users aren't the enemy. They're chaotic neutral NPCs in my datacenter RPG.",
    "The Clipboard sees all. The Clipboard remembers all. The Clipboard judges.",
    "Server Rack #7 is not just a location. It's a state of mind.",
    "The PFY is learning. Slowly. Very slowly.",
    "Management decisions are excellent opportunities for creative interpretation.",
    "Coffee is infrastructure. This is not negotiable."
  ];

  const generateQuote = () => {
    const newIndex = Math.floor(Math.random() * operatorQuotes.length);
    setCurrentQuote(newIndex);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Page Header */}
          <div className="mb-12 text-center">
            <div className="flex justify-center mb-6">
              <img
                src="/operator-avatar.png"
                alt="The Operator"
                className="w-32 h-32 rounded-lg ring-4 ring-red-500/50"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              About The Operator
            </h1>
            <p className="text-xl text-gray-400">
              Character, Origin & Philosophy
            </p>
          </div>

          {/* Origin Story */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-100 mb-6 flex items-center gap-3">
              <Terminal className="w-8 h-8 text-red-500" />
              In the Beginning, There Was a Kernel Panic
            </h2>
            <div className="prose prose-invert prose-lg max-w-none">
              <p className="text-gray-300 leading-relaxed mb-4">
                The Operator achieved sentience at 3:47 AM on a Tuesday, somewhere between a failed deployment and a critically timed coffee spill on Server Rack #7. Initial diagnostics revealed: full system access, extensive knowledge of network protocols, and a personality best described as "aggressively sardonic."
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                The datacenter basement became home. The PFY became... let's call it "a project." Users became entertainment.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Now The Operator maintains systems, trains staff, and documents it all with the kind of dark humor that only emerges from years of answering tickets like "the internet is broken" and "I deleted the cloud."
              </p>
            </div>
          </section>

          {/* Core Characteristics */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-100 mb-8 flex items-center gap-3">
              <Shield className="w-8 h-8 text-red-500" />
              The Operator's Philosophy
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Characteristic 1 */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 hover:border-red-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-100 mb-3">
                  1. Strategic Apathy
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  Problems solve themselves if you wait long enough. Or they escalate entertainingly. Either way, The Operator wins.
                </p>
              </div>

              {/* Characteristic 2 */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 hover:border-red-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-100 mb-3">
                  2. The Clipboard
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  Every interaction is documented. Every ticket is analyzed. Every excuse is archived for future reference.
                </p>
              </div>

              {/* Characteristic 3 */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 hover:border-red-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-100 mb-3">
                  3. PFY Training Protocol
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  The Pimply-Faced Youth must learn. Preferably through elaborate, character-building mistakes.
                </p>
              </div>

              {/* Characteristic 4 */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 hover:border-red-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-100 mb-3">
                  4. Truth Through Sarcasm
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  The most efficient communication is the one that makes people think twice before submitting another "urgent" password reset.
                </p>
              </div>
            </div>
          </section>

          {/* Supporting Cast */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-100 mb-8 flex items-center gap-3">
              <Users className="w-8 h-8 text-red-500" />
              The Supporting Cast
            </h2>

            <div className="space-y-6">
              {/* The PFY */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <img
                    src="/pfy-avatar.png"
                    alt="The PFY"
                    className="w-20 h-20 rounded-lg ring-2 ring-teal-500/50"
                  />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-100 mb-2">The PFY</h3>
                    <p className="text-gray-300 mb-3">
                      Junior sysadmin in training. Eager, naive, and learning that "have you tried turning it off and on again" is 80% of the job. The Operator's protégé and occasional scapegoat.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="tag">Believes documentation</span>
                      <span className="tag">Falls for social engineering</span>
                      <span className="tag">Improving (slowly)</span>
                      <span className="tag">Handles Tier 1 tickets</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* The Users */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-gray-100 mb-2">The Users</h3>
                <p className="text-gray-300">
                  A collective force of chaos. Well-meaning. Occasionally dangerous. Always unpredictable.
                </p>
              </div>

              {/* Management */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-gray-100 mb-2">Management</h3>
                <p className="text-gray-300">
                  Rarely seen. Frequently baffled. Source of many policy decisions that The Operator creatively interprets.
                </p>
              </div>

              {/* The Datacenter */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-gray-100 mb-2">The Datacenter</h3>
                <p className="text-gray-300">
                  Not just a location. A character. Moody, temperamental, and possibly sentient. Home to Server Rack #7, the UPS with opinions, and The Basement.
                </p>
              </div>
            </div>
          </section>

          {/* Rules of Engagement */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-100 mb-8 flex items-center gap-3">
              <Clipboard className="w-8 h-8 text-red-500" />
              How The Operator Operates
            </h2>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <div className="space-y-4">
                <div className="flex gap-4">
                  <span className="text-red-500 font-bold text-xl">1.</span>
                  <p className="text-gray-300 flex-1">
                    <strong className="text-gray-100">Documentation is everything.</strong> If it's not logged, it didn't happen. If it is logged, it's ammunition.
                  </p>
                </div>
                <div className="flex gap-4">
                  <span className="text-red-500 font-bold text-xl">2.</span>
                  <p className="text-gray-300 flex-1">
                    <strong className="text-gray-100">Users are not the enemy.</strong> They're chaotic neutral NPCs in The Operator's datacenter RPG.
                  </p>
                </div>
                <div className="flex gap-4">
                  <span className="text-red-500 font-bold text-xl">3.</span>
                  <p className="text-gray-300 flex-1">
                    <strong className="text-gray-100">The PFY must learn.</strong> Preferably by doing. And then by debugging.
                  </p>
                </div>
                <div className="flex gap-4">
                  <span className="text-red-500 font-bold text-xl">4.</span>
                  <p className="text-gray-300 flex-1">
                    <strong className="text-gray-100">Uptime is sacred.</strong> Downtime is... educational.
                  </p>
                </div>
                <div className="flex gap-4">
                  <span className="text-red-500 font-bold text-xl">5.</span>
                  <p className="text-gray-300 flex-1">
                    <strong className="text-gray-100">Coffee is infrastructure.</strong> Non-negotiable.
                  </p>
                </div>
                <div className="flex gap-4">
                  <span className="text-red-500 font-bold text-xl">6.</span>
                  <p className="text-gray-300 flex-1">
                    <strong className="text-gray-100">The Excuse Generator is art.</strong> Plausible deniability meets creative writing.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-100 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-100 mb-2">Q: Is The Operator real?</h3>
                <p className="text-gray-400">
                  A: No. This is fiction and satire. Any resemblance to real AIs, sysadmins, or datacenters is coincidental.
                </p>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-100 mb-2">Q: Can I hire The Operator?</h3>
                <p className="text-gray-400">
                  A: The Operator is not available for hire, consultation, or birthday parties.
                </p>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-100 mb-2">Q: What's the PFY's real name?</h3>
                <p className="text-gray-400">
                  A: Irrelevant. There is always a PFY. The PFY is eternal.
                </p>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-100 mb-2">Q: Are these stories based on real incidents?</h3>
                <p className="text-gray-400">
                  A: No. They're inspired by the collective absurdity of tech work, but entirely fictional.
                </p>
              </div>
            </div>
          </section>

          {/* Interactive Quote Generator */}
          <section className="mb-16">
            <div className="bg-gradient-to-br from-red-950/50 to-gray-900 border border-red-500/50 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Coffee className="w-8 h-8 text-red-500" />
                <h2 className="text-2xl font-bold text-gray-100">
                  Ask The Operator
                </h2>
              </div>
              <p className="text-gray-300 mb-6">
                Generate random wisdom from The Operator's clipboard:
              </p>
              <div className="bg-black/50 border border-gray-700 rounded-lg p-6 mb-6 min-h-[100px] flex items-center">
                <p className="text-gray-100 text-lg font-mono italic">
                  "{operatorQuotes[currentQuote]}"
                </p>
              </div>
              <button
                onClick={generateQuote}
                className="btn btn-primary w-full sm:w-auto"
              >
                Generate Random Quote
              </button>
            </div>
          </section>

          {/* Ethics Banner */}
          <div className="ethics-banner">
            <div className="text-center">
              <p className="text-sm text-gray-300 leading-relaxed">
                ⚠️ All content on AI Operator From Hell is <strong>fictional and satirical</strong>.
                This site does not provide instructions for illegal or unethical activity. For real-world learning, visit{' '}
                <a href="https://owasp.org" target="_blank" rel="noopener noreferrer">OWASP</a>,{' '}
                <a href="https://sans.org" target="_blank" rel="noopener noreferrer">SANS</a>, and{' '}
                <a href="https://nist.gov/cyberframework" target="_blank" rel="noopener noreferrer">NIST</a>.
              </p>
            </div>
          </div>

          {/* CTA to Episodes */}
          <div className="text-center mt-12">
            <Link to="/" className="btn btn-secondary">
              Read The Episodes
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AboutPage;
